import { TZDate, tz as tzFn } from '@date-fns/tz';
import type { LatLong } from '@iheartjane/geolocation';
import type { Nullable, Nullish, TimeZone } from '@iheartjane/types';
import { timezone, timezoneToken } from '@iheartjane/utils';
import { add, format, isAfter, isBefore, isSameDay } from 'date-fns';

import type { BloomMenuStore } from '@jane/bloom/types/schemas/roots/bloom/menu';
import type {
  TimeRange,
  WorkingHours,
} from '@jane/bloom/types/schemas/roots/bloom/schedule';
import { openingAndClosing } from '@jane/bloom/utils/dates/openingAndClosing';
import { timeRangeForToday } from '@jane/bloom/utils/dates/timeRangeForToday';
import { getDistance } from '@jane/bloom/utils/geo/getDistance';
import type {
  StoreViewModel,
  WorkingHourDetails,
} from '@jane/bloom/viewModels/store';

type GetStoresWithDetailsArgs = {
  stores: BloomMenuStore[];
  userLocation: Partial<LatLong>;
};

export function formatTime(date: Date, timezone: TimeZone) {
  const hours = format(date, 'h', { in: tzFn(timezone) });
  const minutes = format(date, 'mm', { in: tzFn(timezone) });
  const period = format(date, 'aaaaa', { in: tzFn(timezone) });

  // Only include minutes if they're not '00'
  const timeString =
    minutes === '00' ? `${hours}${period}` : `${hours}:${minutes}${period}`;

  return timeString;
}

export function formatDay(day: string) {
  if (!day) return '';
  return day.charAt(0).toUpperCase() + day.slice(1, 3);
}

export function isOpenNow(openingHours: Date, closingHours: Date, now: Date) {
  const opening = new Date(openingHours);
  const closing = new Date(closingHours);

  return isAfter(now, opening) && isBefore(now, closing);
}

export type NextOpening = {
  day: string;
  /** Whole days from `now`; 0 when the store opens later the same day. */
  daysFromNow: number;
  period: {
    from: string;
    to: string;
  };
};

function toNextOpening(
  workingHours: Nullish<WorkingHours>,
  daysFromNow: number
): NextOpening | null {
  const from = workingHours?.period.from;
  const to = workingHours?.period.to;

  if (!workingHours || !from || !to) {
    return null;
  }

  return { day: workingHours.day, daysFromNow, period: { from, to } };
}

export function getNextOpeningHours(
  workingHours: WorkingHours[],
  now: Date,
  willOpenLaterToday: boolean
): NextOpening | null {
  if (!workingHours) {
    return null;
  }

  const currentWeekday = now.getDay();

  // Return current day's working hours if they will open later today
  if (willOpenLaterToday) {
    return toNextOpening(workingHours[currentWeekday], 0);
  }

  // Check the next 6 days for an opening period
  for (let daysFromNow = 1; daysFromNow <= 6; daysFromNow++) {
    const nextOpening = toNextOpening(
      workingHours[(currentWeekday + daysFromNow) % workingHours.length],
      daysFromNow
    );

    if (nextOpening) {
      return nextOpening;
    }
  }

  // If no opening is found, return null
  return null;
}

function getNextOpeningTime(
  workingHours: WorkingHours[],
  ianaTz: TimeZone,
  willOpenLaterToday: boolean
): WorkingHourDetails {
  const zonedNow = new TZDate(new Date(), ianaTz);

  const nextOpening = getNextOpeningHours(
    workingHours,
    zonedNow,
    willOpenLaterToday
  );

  if (!nextOpening) {
    return {
      formattedHours: '',
      nextAvailableClosingTime: null,
      nextAvailableOpeningTime: null,
      openNow: false,
    };
  }

  const [nextOpeningTime, nextClosingTime] = openingAndClosing(
    nextOpening.period,
    ianaTz,
    add(zonedNow, { days: nextOpening.daysFromNow })
  );

  const nextOpeningTimeFormatted = formatTime(nextOpeningTime, ianaTz);
  /** Dated in the store's zone so a late-night opening can't land on the viewer's yesterday. */
  const nextOpeningDateFormatted = format(nextOpeningTime, 'MM/dd', {
    in: tzFn(ianaTz),
  });

  return {
    formattedHours: `Closed until ${formatDay(nextOpening.day)}, ${nextOpeningTimeFormatted} ${timezoneToken(
      ianaTz
    )}`,
    nextAvailableClosingTime: formatTime(nextClosingTime, ianaTz),
    /** Always dated — "closed until 9a" is ambiguous when the store reopens days from now. */
    nextAvailableOpeningTime: `${nextOpeningDateFormatted} ${nextOpeningTimeFormatted}`,
    openNow: false,
  };
}

function workingHourDetails(
  timeRange: Nullable<TimeRange>,
  tz: TimeZone,
  workingHours: WorkingHours[]
): WorkingHourDetails {
  const ianaTz = timezone(tz);
  const zonedNow = new TZDate(new Date(), ianaTz);

  let opening: Date | undefined;
  let closing: Date | undefined;

  if (timeRange && timeRange?.from && timeRange?.to) {
    [opening, closing] = openingAndClosing(
      timeRange as { from: string; to: string },
      tz
    );
  }

  const willOpenLaterToday =
    opening && isAfter(opening, zonedNow) && isSameDay(opening, zonedNow);

  if (opening && closing && isOpenNow(opening, closing, zonedNow)) {
    return {
      formattedHours: `Open until ${formatTime(closing, ianaTz)} ${timezoneToken(ianaTz)}`,
      nextAvailableClosingTime: null,
      nextAvailableOpeningTime: null,
      openNow: true,
    };
  }

  return getNextOpeningTime(workingHours, ianaTz, !!willOpenLaterToday);
}

export function getStoresWithDetails({
  stores,
  userLocation,
}: GetStoresWithDetailsArgs): StoreViewModel[] {
  return stores.map((store) => {
    const todaysHours = timeRangeForToday(
      store.working_hours,
      store.time_zone_identifier
    );

    let distance;
    const d = getDistance(
      userLocation?.lat,
      userLocation?.long,
      store.latitude,
      store.longitude
    );
    if (isNaN(d)) {
      distance = '';
    } else {
      distance = `${d.toFixed(d > 20 ? 0 : 1)}mi`;
    }

    return {
      ...store,
      ...workingHourDetails(
        todaysHours,
        store.time_zone_identifier,
        store.working_hours || []
      ),
      distance,
    };
  });
}
